import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ConfirmModal from '../components/ConfirmModal';
import { PageHeader, MonoLabel, EditorialButton } from '../components/Editorial';
import Button from '../components/Button';

const GRADE_LABELS = ['', 'Interested', 'Evaluating', 'Priority', 'Top Prospect', 'Must Have'];

function StarRating({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '2px' }}>
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(value === star ? null : star)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
            color: star <= (value || 0) ? '#f59e0b' : '#d1d5db', fontSize: '18px', lineHeight: 1,
          }}
          title={GRADE_LABELS[star]}
          aria-label={`Grade ${star}: ${GRADE_LABELS[star]}`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

export default function SavedAthletesPage() {
  const { user, API_URL } = useAuth();
  const navigate = useNavigate();
  const [savedAthletes, setSavedAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [editingNotes, setEditingNotes] = useState({});
  const [confirmAction, setConfirmAction] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchSaved();
  }, []);

  const fetchSaved = async () => {
    try {
      const res = await axios.get(`${API_URL}/saved-athletes/me`, getAxiosConfig());
      setSavedAthletes(res.data || []);
    } catch (err) {
      console.error('Error fetching saved athletes:', err);
    }
    setLoading(false);
  };

  const showMsg = (text, type = '') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleUpdateGrade = async (sa, grade) => {
    try {
      await axios.put(`${API_URL}/saved-athletes/${sa.id}`, { grade }, getAxiosConfig());
      setSavedAthletes(prev => prev.map(a => a.id === sa.id ? { ...a, grade } : a));
    } catch (err) {
      showMsg('Error updating grade', 'error');
    }
  };

  const handleUpdateNotes = async (sa) => {
    const notes = editingNotes[sa.id];
    if (notes === undefined || notes === (sa.notes || '')) return;
    try {
      await axios.put(`${API_URL}/saved-athletes/${sa.id}`, { notes }, getAxiosConfig());
      setSavedAthletes(prev => prev.map(a => a.id === sa.id ? { ...a, notes } : a));
      showMsg('Notes saved');
    } catch (err) {
      showMsg('Error saving notes', 'error');
    }
  };

  const handleDelete = (sa) => {
    setConfirmAction({
      title: 'Remove Athlete',
      message: `Remove ${sa.name} from your saved list?`,
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await axios.delete(`${API_URL}/saved-athletes/${sa.id}`, getAxiosConfig());
          setSavedAthletes(prev => prev.filter(a => a.id !== sa.id));
          showMsg('Athlete removed');
        } catch (err) {
          showMsg('Error removing athlete', 'error');
        }
      },
      onCancel: () => setConfirmAction(null),
    });
  };

  // Filter and sort
  const filtered = savedAthletes.filter(sa => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (sa.name || '').toLowerCase().includes(q) ||
      (sa.high_school || '').toLowerCase().includes(q) ||
      (sa.position_primary || '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'grade') return (b.grade || 0) - (a.grade || 0);
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    return 0; // 'recent' — already sorted by API (created_at desc)
  });

  if (loading) return <div className="settings-page"><p style={{ color: '#64748b' }}>Loading...</p></div>;

  return (
    <div className="ed-page">
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">{message}</div>
      )}

      <PageHeader
        eyebrow={
          <>
            <MonoLabel>LISTS · SAVED ATHLETES</MonoLabel>
            <MonoLabel>·</MonoLabel>
            <MonoLabel>{savedAthletes.length} ATHLETE{savedAthletes.length !== 1 ? 'S' : ''}</MonoLabel>
          </>
        }
        title="Your"
        accent="prospect board."
        sub="Track and compare athletes you've saved. Tap any to open their card."
        actions={
          <EditorialButton variant="solid" onClick={() => navigate('/players')}>
            Find Athletes
          </EditorialButton>
        }
      />

      {savedAthletes.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
          <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#1a202c', marginBottom: '8px' }}>No saved athletes yet</h2>
          <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
            Search for athletes and save them to your list to track and compare.
          </p>
          <Button
            variant="primary"
            onClick={() => navigate('/players')}
          >
            Search Athletes
          </Button>
        </div>
      ) : (
        <>
          {/* Search & Sort Bar */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <input
              type="text"
              className="search-bar"
              placeholder="Search saved athletes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ flex: 1, minWidth: '200px' }}
            />
            <select
              className="form-field__select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ width: '160px' }}
            >
              <option value="recent">Most Recent</option>
              <option value="grade">Highest Grade</option>
              <option value="name">Name A-Z</option>
            </select>
          </div>

          {/* Athletes List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {sorted.map(sa => (
              <div key={sa.id} className="card" style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  {/* Photo */}
                  <div
                    style={{
                      width: '56px', height: '56px', borderRadius: '10px', overflow: 'hidden',
                      background: '#f1f5f9', flexShrink: 0, cursor: 'pointer',
                    }}
                    onClick={() => navigate(`/players?highlight=${sa.athlete_user_id}`)}
                  >
                    {sa.primary_photo ? (
                      <img src={sa.primary_photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '20px', fontWeight: 700 }}>
                        {(sa.name || '?')[0]}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a' }}>{sa.name}</div>
                        <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                          {[sa.position_primary, sa.position_secondary].filter(Boolean).join(' / ')}
                          {sa.high_school && <span> · {sa.high_school}</span>}
                          {sa.state && <span>, {sa.state}</span>}
                          {sa.graduation_year && <span> · Class of {sa.graduation_year}</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                        <StarRating value={sa.grade} onChange={(grade) => handleUpdateGrade(sa, grade)} />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          iconOnly
                          onClick={() => handleDelete(sa)}
                          title="Remove"
                          aria-label={`Remove ${sa.name}`}
                        >
                          ×
                        </Button>
                      </div>
                    </div>

                    {/* Grade label */}
                    {sa.grade && (
                      <span style={{
                        display: 'inline-block', marginTop: '6px', padding: '2px 8px',
                        fontSize: '11px', fontWeight: 600, borderRadius: '10px',
                        background: sa.grade >= 4 ? '#dcfce7' : sa.grade >= 2 ? '#dbeafe' : '#f1f5f9',
                        color: sa.grade >= 4 ? '#166534' : sa.grade >= 2 ? '#1e40af' : '#475569',
                      }}>
                        {GRADE_LABELS[sa.grade]}
                      </span>
                    )}

                    {/* Notes */}
                    <div style={{ marginTop: '8px' }}>
                      <textarea
                        className="form-field__input"
                        rows={1}
                        placeholder="Add notes..."
                        value={editingNotes[sa.id] !== undefined ? editingNotes[sa.id] : (sa.notes || '')}
                        onChange={(e) => setEditingNotes(prev => ({ ...prev, [sa.id]: e.target.value }))}
                        onBlur={() => handleUpdateNotes(sa)}
                        style={{ fontSize: '13px', padding: '6px 10px', resize: 'vertical', minHeight: '32px' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && searchQuery && (
            <div style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
              No athletes match "{searchQuery}"
            </div>
          )}
        </>
      )}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          danger={confirmAction.danger}
          onConfirm={confirmAction.onConfirm}
          onCancel={confirmAction.onCancel}
        />
      )}
    </div>
  );
}
